import { useEffect } from 'react';

interface LightboxImage {
  publicUrl: string;
  caption?: string;
}

interface EventLightboxProps {
  images: LightboxImage[];
  currentIndex: number;
  onClose: () => void;
  onNext: () => void;
  onPrev: () => void;
}

export default function EventLightbox({
  images,
  currentIndex,
  onClose,
  onNext,
  onPrev,
}: EventLightboxProps) {
  const photo = images[currentIndex];

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') onNext();
      if (e.key === 'ArrowLeft') onPrev();
    };
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose, onNext, onPrev]);

  if (!photo) return null;

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative max-w-5xl w-full flex flex-col bg-[#111] rounded-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-xl"></i>
        </button>

        {/* Image */}
        <div className="relative flex items-center justify-center bg-black min-h-[300px]">
          <img
            src={photo.publicUrl}
            alt={photo.caption ?? 'Event photo'}
            className="w-full max-h-[80vh] object-contain"
          />

          {/* Prev */}
          <button
            onClick={onPrev}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>

          {/* Next */}
          <button
            onClick={onNext}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/25 rounded-full text-white transition-colors cursor-pointer"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
        </div>

        {/* Caption Bar */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
          <p className="text-white/80 text-sm font-medium">{photo.caption}</p>
          <span className="text-white/40 text-sm">{currentIndex + 1} / {images.length}</span>
        </div>
      </div>
    </div>
  );
}
